/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * VENTA Module Sidebar Navigation
 *
 * Provides sidebar navigation for the Inventory & Warehouse module
 * with three sections: Inventory, Warehouse, and Configuration.
 */

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package,
  FolderTree,
  BarChart3,
  AlertTriangle,
  Upload,
  MapPin,
  Grid3X3,
  ArrowRightLeft,
  CheckCircle2,
  CreditCard,
  DollarSign,
  Settings,
  SlidersHorizontal,
  Ruler,
  Shield,
  ShieldCheck,
  ChevronDown,
  Users,
  ShoppingBag,
  ShoppingCart,
  FileText,
  Type,
  Receipt,
  Palette,
  Truck,
  Tags,
  PackageCheck,
  RotateCcw,
  Activity,
  Landmark,
  Banknote,
  Smartphone,
  ArrowDownLeft,
  ArrowUpRight,
  Scale,
  History,
  TrendingUp,
  Search,
  Command,
  PieChart,
  ClipboardCheck,
  ClipboardList,
  UserCog,
  Lock,
  Monitor,
  Building2,
  Puzzle,
  BookOpen,
  FileSpreadsheet,
  Target,
  HeartHandshake,
  LayoutGrid,
  List,
  Calendar,
  CalendarX,
  XCircle,
  FileCheck,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useMyPermissions } from '../hooks/useRoles';
import { useMyMenus } from '../hooks/useMenus';
import type { MenuTreeDto } from '../services/roles.service';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/theme';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useSidebarCollapsed } from '@/stores/layoutStore';
import { useSupportSessionStore } from '@/stores/supportSessionStore';
import { useLowStockSummary } from '../hooks/useStock';
import { useTransferSummary } from '../hooks/useTransfers';
import { useHasFeature } from '@/hooks/api/useModuleFeatures';

import logoDark from '@/assets/logos/dark/ventacorex-logo-dark.svg';
import logoLight from '@/assets/logos/light/ventacorex-logo-light.svg';
import { FavoritesContextMenu } from '@/components/layouts/favorites/FavoritesContextMenu';

// ── Icon string → Lucide component mapping ──
const lucideIconMap: Record<string, LucideIcon> = {
  'package': Package, 'folder-tree': FolderTree, 'layers': BarChart3, 'tags': Tags,
  'sliders': SlidersHorizontal, 'alert-triangle': AlertTriangle, 'upload': Upload,
  'map-pin': MapPin, 'grid-3x3': Grid3X3, 'arrow-right-left': ArrowRightLeft,
  'check-circle': CheckCircle2, 'clipboard-check': ClipboardCheck, 'users': Users,
  'monitor': Monitor, 'file-text': FileText, 'clipboard-list': ClipboardList,
  'clipboard': ClipboardList, 'package-check': PackageCheck,
  'undo-2': RotateCcw, 'file-minus': FileText, 'calculator': Receipt,
  'landmark': Landmark, 'smartphone': Smartphone, 'scale': Scale, 'history': History,
  'trending-up': TrendingUp, 'receipt': Receipt, 'credit-card': CreditCard, 'send': ArrowRightLeft,
  'layout-dashboard': BarChart3, 'bar-chart-2': BarChart3, 'activity': Activity,
  'file-bar-chart': PieChart, 'package-search': Package,
  'user-search': Users, 'coins': DollarSign, 'move': ArrowRightLeft, 'calendar-x': CalendarX,
  'shopping-cart': ShoppingCart, 'wallet': Banknote, 'clock': History, 'percent': DollarSign,
  'user-plus': Users, 'shield': Shield, 'shield-check': ShieldCheck, 'hash': Type, 'ruler': Ruler,
  'currency': DollarSign, 'file-type': Palette, 'settings': Settings,
  'monitor-cog': Settings, 'shopping-bag': ShoppingBag, 'truck': Truck,
  'banknote': Banknote, 'pie-chart': PieChart, 'user-cog': UserCog,
  'bar-chart-3': BarChart3, 'arrow-down-left': ArrowDownLeft, 'arrow-up-right': ArrowUpRight,
  'lock': Lock,
  'puzzle': Puzzle,
  'file-spreadsheet': FileSpreadsheet,
  'target': Target,
  'heart-handshake': HeartHandshake,
  'layout-grid': LayoutGrid,
  'list': List,
  'calendar': Calendar,
  'x-circle': XCircle,
  'file-check': FileCheck,
  'shield-alert': ShieldAlert,
};

/** Fallback section code → i18n key (used only if label_key is NULL in DB). */
const SECTION_LABEL_FALLBACK: Record<string, string> = {
  SEC_INVENTORY: 'venta:venta.sidebar.inventory',
  SEC_WAREHOUSE: 'venta:venta.sidebar.warehouse',
  SEC_PARTNERS: 'venta:venta.sidebar.partners',
  SEC_SALES: 'venta:venta.sidebar.sales',
  SEC_PURCHASING: 'venta:venta.sidebar.purchasing',
  SEC_CASHBANK: 'venta:venta.sidebar.cashBank',
  SEC_TRANSACTIONS: 'venta:venta.sidebar.transactions',
  SEC_ANALYTICS: 'venta:venta.sidebar.analytics',
  SEC_REPORTS: 'venta:venta.sidebar.reportsSection',
  SEC_BILLING: 'venta:venta.sidebar.billing',
  SEC_ADMINISTRATION: 'venta:venta.sidebar.administrationSection',
  SEC_CONFIGURATION: 'venta:venta.sidebar.configuration',
  SEC_ACTIVITIES: 'venta:venta.sidebar.activities',
};

/** Qualify a bare i18n key with the venta namespace if it lacks one. */
function qualifyKey(key: string | null | undefined, fallback: string): string {
  if (!key) return fallback;
  return key.includes(':') ? key : `venta:${key}`;
}

/** Map API MenuTreeDto[] to NavSection[] format for rendering. */
/** Menu items that should only be visible to ORG_ADMIN / SUPER_ADMIN. */
const ORG_ADMIN_ONLY_CODES = new Set(['ROLES', 'JOURNAL_ENTRIES', 'INV_REPRICING', 'SUPPORT_ACCESS']);

function mapMenuTreeToSections(
  tree: MenuTreeDto[],
  t: (key: string) => string,
  alertCount: number,
  pendingCount: number,
): NavSection[] {
  return tree.map((section) => ({
    id: section.code,
    titleKey: qualifyKey(section.labelKey, SECTION_LABEL_FALLBACK[section.code] ?? section.code),
    icon: lucideIconMap[section.icon] || Package,
    items: section.items.map((item) => {
      const navItem: NavItem = {
        labelKey: qualifyKey(item.labelKey, item.labelKey),
        href: item.route || '#',
        icon: lucideIconMap[item.icon] || Package,
        feature: item.feature || undefined,
        orgAdminOnly: ORG_ADMIN_ONLY_CODES.has(item.code) || undefined,
      };
      // Inject dynamic badges
      if (item.code === 'STOCK_ALERTS' && alertCount > 0) {
        navItem.badge = alertCount;
        navItem.badgeVariant = 'danger';
      }
      if (item.code === 'TRANSFER_APPROVALS' && pendingCount > 0) {
        navItem.badge = pendingCount;
        navItem.badgeVariant = 'danger';
      }
      return navItem;
    }),
  }));
}

interface NavItem {
  labelKey: string;
  href: string;
  icon: LucideIcon;
  badge?: number;
  badgeVariant?: 'default' | 'danger';
  disabled?: boolean;
  feature?: string;
  /** Permission code required to see this item (e.g., "INVENTORY:ADJUST"). */
  requiredPermission?: string;
  /** If true, only ORG_ADMIN (or SUPER_ADMIN) can see this item. */
  orgAdminOnly?: boolean;
  /** If true, the item requires an unsubscribed addon — show lock + "Paid Feature" dialog. */
  locked?: boolean;
}

interface NavSection {
  id: string;
  titleKey: string;
  icon?: LucideIcon;
  items: NavItem[];
  /** Permission code required to see this section (e.g., "SALES:VIEW"). */
  requiredPermission?: string;
}

/**
 * Permission-based section visibility.
 * Each section/item maps to a permission code from sec_permissions.
 * The user's effective permissions are fetched from GET /api/v1/venta/me/permissions
 * and stored in authStore. Changes made via SuperAdmin role templates or org-level
 * role management propagate automatically on next login or permission refresh.
 */

const navSections: NavSection[] = [
  {
    id: 'organization',
    titleKey: 'venta:venta.sidebar.myOrganization',
    icon: Building2,
    requiredPermission: 'SETTINGS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.myOrganization', href: '/venta/my-organization', icon: Building2 },
    ],
  },
  {
    id: 'inventory',
    titleKey: 'venta:venta.sidebar.inventory',
    icon: Package,
    requiredPermission: 'PRODUCTS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.inventoryDashboard', href: '/venta/inventory-dashboard', icon: BarChart3, requiredPermission: 'RPT_INV_VALUATION:VIEW', feature: 'INVENTORY' },
      { labelKey: 'venta:venta.sidebar.products', href: '/venta/products', icon: Package, requiredPermission: 'PRODUCTS:VIEW' },
      { labelKey: 'venta:venta.sidebar.categories', href: '/venta/categories', icon: FolderTree, requiredPermission: 'PRODUCTS:VIEW' },
      { labelKey: 'venta:venta.sidebar.stockLevels', href: '/venta/stock', icon: BarChart3, requiredPermission: 'INVENTORY:VIEW', feature: 'INVENTORY' },
      { labelKey: 'venta:venta.sidebar.stockAdjust', href: '/venta/stock/adjust', icon: SlidersHorizontal, requiredPermission: 'INVENTORY:ADJUST', feature: 'INVENTORY' },
      { labelKey: 'venta:venta.sidebar.lowStockAlerts', href: '/venta/stock/alerts', icon: AlertTriangle, badgeVariant: 'danger', requiredPermission: 'INVENTORY:VIEW', feature: 'INVENTORY' },
      { labelKey: 'venta:venta.sidebar.importProducts', href: '/venta/products/import', icon: Upload, requiredPermission: 'PRODUCTS:IMPORT' },
      { labelKey: 'venta:venta.sidebar.importSupplierPrices', href: '/venta/products/supplier-prices/import', icon: Upload, requiredPermission: 'PRODUCTS:IMPORT' },
    ],
  },
  {
    id: 'warehouse',
    titleKey: 'venta:venta.sidebar.warehouse',
    icon: MapPin,
    requiredPermission: 'LOCATIONS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.locations', href: '/venta/locations', icon: MapPin, requiredPermission: 'LOCATIONS:VIEW' },
      { labelKey: 'venta:venta.sidebar.stockMatrix', href: '/venta/stock/matrix', icon: Grid3X3, requiredPermission: 'INVENTORY:VIEW', feature: 'INVENTORY' },
      { labelKey: 'venta:venta.sidebar.transfers', href: '/venta/transfers/new', icon: ArrowRightLeft, requiredPermission: 'INVENTORY:TRANSFER', feature: 'INVENTORY' },
      { labelKey: 'venta:venta.sidebar.approvals', href: '/venta/transfers/approvals', icon: CheckCircle2, requiredPermission: 'INVENTORY:TRANSFER', feature: 'INVENTORY' },
      { labelKey: 'venta:venta.sidebar.inventoryCount', href: '/venta/inventory-counts', icon: ClipboardCheck, feature: 'INVENTORY_COUNT', requiredPermission: 'INVENTORY:COUNT' },
      { labelKey: 'venta:venta.sidebar.activityLog', href: '/venta/inventory-activity', icon: Activity, requiredPermission: 'STOCK:VIEW', feature: 'INVENTORY' },
    ],
  },
  // ── PARTNERS ──
  {
    id: 'partners',
    titleKey: 'venta:venta.sidebar.partners',
    icon: Users,
    requiredPermission: 'PARTNERS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.partners', href: '/venta/partners', icon: Users },
    ],
  },

  // ── SALES ──
  {
    id: 'sales',
    titleKey: 'venta:venta.sidebar.sales',
    icon: ShoppingBag,
    requiredPermission: 'SALES:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.pos', href: '/venta/pos', icon: Monitor, feature: 'POS' },
      { labelKey: 'venta:venta.sidebar.quotations', href: '/venta/quotations', icon: FileText },
      { labelKey: 'venta:venta.sidebar.salesOrders', href: '/venta/sales-orders', icon: ShoppingCart },
      { labelKey: 'venta:venta.sidebar.invoices', href: '/venta/invoices', icon: Receipt },
    ],
  },

  // ── DEALS ──
  {
    id: 'deals',
    titleKey: 'venta:venta.sidebar.deals',
    icon: Target,
    requiredPermission: 'DEALS_CONFIG:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.dealsList', href: '/venta/deals', icon: HeartHandshake, feature: 'DEAL_PIPELINE', requiredPermission: 'DEALS:VIEW' },
      { labelKey: 'venta:venta.sidebar.pipelineBoard', href: '/venta/deals/pipeline', icon: LayoutGrid, feature: 'DEAL_PIPELINE', requiredPermission: 'DEALS:VIEW' },
      { labelKey: 'venta:venta.sidebar.forecast', href: '/venta/deals/forecast', icon: BarChart3, feature: 'DEAL_PIPELINE', requiredPermission: 'DEALS:VIEW' },
      { labelKey: 'venta:venta.sidebar.lostAnalytics', href: '/venta/deals/analytics', icon: PieChart, feature: 'DEAL_PIPELINE', requiredPermission: 'DEALS:VIEW' },
      { labelKey: 'venta:venta.sidebar.dealStages', href: '/venta/deals/stages', icon: Target, feature: 'DEAL_PIPELINE', requiredPermission: 'DEALS_CONFIG:VIEW' },
      { labelKey: 'venta:venta.sidebar.lostReasons', href: '/venta/deals/lost-reasons', icon: FileText, feature: 'DEAL_PIPELINE', requiredPermission: 'DEALS_CONFIG:VIEW' },
    ],
  },

  // ── ACTIVITIES & TASKS ──
  {
    id: 'activities',
    titleKey: 'venta:venta.sidebar.activities',
    icon: List,
    requiredPermission: 'ACTIVITIES_CONFIG:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.activityList', href: '/venta/activities', icon: List, feature: 'ACTIVITIES_TASKS', requiredPermission: 'ACTIVITIES_CONFIG:VIEW' },
      { labelKey: 'venta:venta.sidebar.activityCalendar', href: '/venta/activities/calendar', icon: Calendar, feature: 'ACTIVITIES_TASKS', requiredPermission: 'ACTIVITIES_CONFIG:VIEW' },
      { labelKey: 'venta:venta.sidebar.taskBoard', href: '/venta/tasks', icon: ClipboardList, feature: 'ACTIVITIES_TASKS', requiredPermission: 'ACTIVITIES_CONFIG:VIEW' },
      { labelKey: 'venta:venta.sidebar.activitySettings', href: '/venta/activities/settings', icon: Settings, feature: 'ACTIVITIES_TASKS', requiredPermission: 'ACTIVITIES_CONFIG:VIEW' },
    ],
  },

  // ── PURCHASING ──
  {
    id: 'purchasing',
    titleKey: 'venta:venta.sidebar.purchasing',
    icon: Truck,
    requiredPermission: 'PURCHASING:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.purchaseOrders', href: '/venta/purchase-orders', icon: ShoppingCart },
      { labelKey: 'venta:venta.sidebar.poInvoices', href: '/venta/purchase-invoices', icon: FileCheck },
      { labelKey: 'venta:venta.sidebar.goodsReceipts', href: '/venta/goods-receipts', icon: PackageCheck },
      { labelKey: 'venta:venta.sidebar.purchaseReturns', href: '/venta/purchase-returns', icon: RotateCcw },
      { labelKey: 'venta:venta.debitNotes.sidebar', href: '/venta/purchasing/debit-notes', icon: FileText },
      { labelKey: 'venta:venta.sidebar.costSheets', href: '/venta/cost-sheets', icon: FileSpreadsheet, requiredPermission: 'COST_SHEETS:VIEW' },
    ],
  },

  // ── CASH & BANK ──
  {
    id: 'cashbank',
    titleKey: 'venta:venta.sidebar.cashBank',
    icon: Banknote,
    requiredPermission: 'CASHBANK:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.cashRegisters', href: '/venta/cash-registers', icon: Banknote, requiredPermission: 'CASHBANK:VIEW' },
      { labelKey: 'venta:venta.sidebar.bankAccounts', href: '/venta/bank-accounts', icon: Landmark, requiredPermission: 'CASHBANK:VIEW' },
      { labelKey: 'venta:venta.sidebar.mobileMoney', href: '/venta/mobile-money', icon: Smartphone, requiredPermission: 'CASHBANK:VIEW' },
      { labelKey: 'venta:venta.mobileMoney.transactions.title', href: '/venta/mobile-money/transactions', icon: Smartphone, requiredPermission: 'CASHBANK:VIEW' },
      { labelKey: 'venta:venta.sidebar.reconciliation', href: '/venta/reconciliation', icon: Scale, requiredPermission: 'CASHBANK:RECONCILE' },
      { labelKey: 'venta:venta.sidebar.reconciliationHistory', href: '/venta/reconciliation/history', icon: History, requiredPermission: 'CASHBANK:RECONCILE' },
      { labelKey: 'venta:venta.sidebar.cashFlow', href: '/venta/cash-flow', icon: TrendingUp, requiredPermission: 'CASHBANK:VIEW' },
    ],
  },

  // ── TRANSACTIONS ──
  {
    id: 'transactions',
    titleKey: 'venta:venta.sidebar.transactions',
    icon: ArrowRightLeft,
    requiredPermission: 'CASHBANK:CREATE',
    items: [
      { labelKey: 'venta:venta.sidebar.cashReceipt', href: '/venta/cash-receipts/new', icon: ArrowDownLeft, requiredPermission: 'CASHBANK:CREATE' },
      { labelKey: 'venta:venta.sidebar.paymentVoucher', href: '/venta/payment-vouchers/new', icon: ArrowUpRight, requiredPermission: 'CASHBANK:CREATE' },
      { labelKey: 'venta:venta.sidebar.transfer', href: '/venta/bank-transfers/new', icon: ArrowRightLeft, requiredPermission: 'CASHBANK:TRANSFER' },
    ],
  },

  // ── ANALYTICS ──
  {
    id: 'analytics',
    titleKey: 'venta:venta.sidebar.analytics',
    icon: BarChart3,
    requiredPermission: 'REPORTS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.salesDashboard', href: '/venta/sales-dashboard', icon: BarChart3 },
      { labelKey: 'venta:venta.sidebar.salesReports', href: '/venta/sales-reports', icon: FileText, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.businessPulse', href: '/venta/analytics/pulse', icon: Activity, feature: 'SALES_REPORTS' },
    ],
  },

  // ── REPORTS ──
  {
    id: 'reports',
    titleKey: 'venta:venta.sidebar.reportsSection',
    icon: PieChart,
    requiredPermission: 'REPORTS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.reportsSalesOverview', href: '/venta/reports/sales', icon: BarChart3, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsByProduct', href: '/venta/reports/sales/by-product', icon: Package, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsByCustomer', href: '/venta/reports/sales/by-customer', icon: Users, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsInventoryValuation', href: '/venta/reports/inventory/valuation', icon: ClipboardList, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.rptInvExpiry', href: '/venta/reports/inventory/expiry', icon: CalendarX, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsStockMovement', href: '/venta/reports/inventory/movement', icon: ArrowRightLeft, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsPurchases', href: '/venta/reports/financial/purchases', icon: ShoppingCart, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsCashFlow', href: '/venta/reports/financial/cash-flow', icon: TrendingUp, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsAging', href: '/venta/reports/financial/aging', icon: Activity, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.reportsTax', href: '/venta/reports/financial/tax', icon: DollarSign, feature: 'SALES_REPORTS' },
      { labelKey: 'venta:venta.sidebar.repricing', href: '/venta/reports/inventory/repricing', icon: Tags, requiredPermission: 'RPT_INV_VALUATION:VIEW' },
    ],
  },

  // ── BILLING ──
  {
    id: 'billing',
    titleKey: 'venta:venta.sidebar.billing',
    icon: CreditCard,
    requiredPermission: 'SETTINGS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.subscription', href: '/venta/billing', icon: CreditCard },
      { labelKey: 'venta:venta.sidebar.addons', href: '/venta/billing/addons', icon: Puzzle },
    ],
  },

  // ── ADMINISTRATION ──
  {
    id: 'administration',
    titleKey: 'venta:venta.sidebar.administrationSection',
    icon: UserCog,
    requiredPermission: 'USERS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.adminUsers', href: '/venta/admin/users', icon: Users },
      { labelKey: 'venta:venta.sidebar.adminRoles', href: '/venta/admin/roles', icon: Lock, orgAdminOnly: true },
      { labelKey: 'venta:venta.sidebar.journalEntries', href: '/venta/journal/entries', icon: BookOpen, orgAdminOnly: true },
    ],
  },

  // ── CONFIGURATION ──
  {
    id: 'configuration',
    titleKey: 'venta:venta.sidebar.configuration',
    icon: Settings,
    requiredPermission: 'SETTINGS:VIEW',
    items: [
      { labelKey: 'venta:venta.sidebar.taxConfig', href: '/venta/tax-config', icon: DollarSign },
      { labelKey: 'venta:venta.sidebar.docNumbering', href: '/venta/doc-numbering', icon: Type },
      { labelKey: 'venta:venta.sidebar.unitsOfMeasure', href: '/venta/units-of-measure', icon: Ruler },
      { labelKey: 'venta:venta.sidebar.fxRates', href: '/venta/fx-rates', icon: DollarSign },
      { labelKey: 'venta:venta.sidebar.invoiceTemplates', href: '/venta/invoice-templates', icon: Palette },
      { labelKey: 'venta:venta.sidebar.settings', href: '/venta/settings', icon: Settings },
      { labelKey: 'venta:venta.sidebar.priceTiers', href: '/venta/price-tiers', icon: Tags, requiredPermission: 'PRICE_TIERS:VIEW' },
      { labelKey: 'venta:venta.sidebar.expenseTypes', href: '/venta/settings/expense-types', icon: FileSpreadsheet, requiredPermission: 'SETTINGS:VIEW' },
      { labelKey: 'venta:venta.sidebar.posSettings', href: '/venta/pos/settings', icon: Settings, feature: 'POS' },
      { labelKey: 'venta:venta.sidebar.offlineTills', href: '/venta/pos/offline-tills', icon: ShieldAlert, feature: 'POS', requiredPermission: 'POS_OFFLINE_MONITOR:VIEW' },
      { labelKey: 'venta:venta.sidebar.supportAccess', href: '/venta/support-access', icon: Shield, orgAdminOnly: true },
      { labelKey: 'venta:venta.sidebar.accountSecurity', href: '/venta/account-security', icon: ShieldCheck },
    ],
  },
];

// Export for FavoritesBar lookup
export type { NavItem };
export { navSections };

/** Find any nav item by its href across all sections. Returns undefined if not found. */
export function findNavItemByHref(href: string): NavItem | undefined {
  for (const section of navSections) {
    const found = section.items.find((item) => item.href === href);
    if (found) return found;
  }
  return undefined;
}

/**
 * VentaSidebarContent — just the nav sections (no header, no footer, no container).
 * Used by ModuleShell which provides its own header/footer generically.
 */
export function VentaSidebarContent() {
  const { t } = useTranslation('venta');
  const location = useLocation();
  const setBottomNavSections = useUIStore((s) => s.setBottomNavSections);
  const isSupportSession = !!useSupportSessionStore((s) => s.activeSession);
  const { hasFeature } = useHasFeature();
  const { user } = useAuthStore();
  const isOrgAdmin = user?.role === 'ORG_ADMIN' || user?.role === 'SUPER_ADMIN';

  // Load permissions from backend into authStore (for PermissionGate usage elsewhere)
  useMyPermissions();

  // Fetch menu tree from API (already permission-filtered server-side)
  const { data: menuTree } = useMyMenus();

  const { data: alertSummary } = useLowStockSummary();
  const alertCount = alertSummary?.totalAlerts ?? 0;

  const { data: transferSummary } = useTransferSummary();
  const pendingCount = transferSummary?.pendingApprovals ?? 0;

  const enrichedSections = useMemo(() => {
    if (!menuTree) return [];
    const apiSections = mapMenuTreeToSections(menuTree, t, alertCount, pendingCount);
    return apiSections
      .map((section) => ({
        ...section,
        items: section.items
          .filter((item) => !(isSupportSession && item.href === '/venta/support-access'))
          .filter((item) => !item.orgAdminOnly || isOrgAdmin)
          .filter((item) => isOrgAdmin || !item.feature || hasFeature('VENTA', item.feature))
          .map((item) => ({
            ...item,
            locked: isOrgAdmin && !!item.feature && !hasFeature('VENTA', item.feature),
          })),
      }))
      .filter((section) => section.items.length > 0);
  }, [menuTree, t, alertCount, pendingCount, isSupportSession, hasFeature, isOrgAdmin]);

  useEffect(() => {
    setBottomNavSections(enrichedSections);
  }, [enrichedSections, setBottomNavSections]);

  return (
    <div className="venta-sidebar-nav">
      {/* Search trigger */}
      <div className="venta-sidebar-search">
        <button
          className="venta-search-trigger"
          onClick={() => {
            window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));
          }}
        >
          <Search size={14} />
          <span>{t('venta:venta.sidebar.quickSearch', 'Quick search...')}</span>
          <kbd className="venta-search-kbd"><Command size={10} />K</kbd>
        </button>
      </div>

      {enrichedSections.map((section) => (
        <SidebarSection
          key={section.id}
          id={section.id}
          title={t(section.titleKey)}
          items={section.items}
          currentPath={location.pathname}
        />
      ))}

      <style>{`
        .venta-sidebar-search {
          padding: 8px 8px 4px;
        }

        .venta-search-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 7px 10px;
          background: var(--color-bg-primary);
          border: 1px solid var(--color-border-subtle);
          border-radius: 8px;
          color: var(--color-text-muted);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .venta-search-trigger:hover {
          border-color: var(--color-border-default);
          color: var(--color-text-secondary);
        }

        .venta-search-kbd {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 2px;
          padding: 1px 5px;
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-subtle);
          border-radius: 4px;
          font-family: inherit;
          font-size: 10px;
        }

        .venta-sidebar-section {
          margin-bottom: 0.25rem;
        }

        .venta-sidebar-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.375rem 0.75rem;
          font-size: 0.675rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-muted);
          cursor: pointer;
          border-radius: 0.375rem;
          transition: color 0.15s;
          user-select: none;
        }

        .venta-sidebar-section-header:hover {
          color: var(--color-text-secondary);
        }

        .venta-sidebar-section-header .chevron {
          transition: transform 0.2s ease;
        }

        .venta-sidebar-section-header .chevron.collapsed {
          transform: rotate(-90deg);
        }

        .venta-sidebar-item {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.5rem 0.75rem;
          padding-left: 1rem;
          font-size: 0.8125rem;
          color: var(--color-text-secondary);
          text-decoration: none;
          border-radius: 0.375rem;
          transition: all 0.15s ease;
          border-left: 2px solid transparent;
        }

        .venta-sidebar-item:hover {
          background: var(--sidebar-item-hover, var(--color-bg-hover));
          color: var(--color-text-primary);
        }

        .venta-sidebar-item.active {
          background: var(--sidebar-item-active, rgba(139, 0, 0, 0.1));
          color: var(--sidebar-item-active-text, var(--color-brand-primary));
          border-left-color: var(--sidebar-item-active-border, var(--color-brand-primary));
          font-weight: 500;
        }

        .venta-sidebar-item-icon {
          flex-shrink: 0;
          opacity: 0.7;
        }

        .venta-sidebar-item.active .venta-sidebar-item-icon {
          opacity: 1;
        }

        .venta-sidebar-item-label {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .venta-sidebar-badge {
          font-size: 0.6875rem;
          font-weight: 600;
          padding: 0.0625rem 0.375rem;
          border-radius: 9999px;
          background: var(--color-bg-tertiary);
          color: var(--color-text-muted);
          min-width: 1.25rem;
          text-align: center;
        }

        .venta-sidebar-badge.danger {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .venta-sidebar-item.disabled {
          opacity: 0.45;
          cursor: not-allowed;
          pointer-events: none;
        }

        .venta-sidebar-item.locked {
          opacity: 0.5;
          cursor: pointer;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          font: inherit;
        }
        .venta-sidebar-item.locked:hover {
          opacity: 0.7;
          background: var(--color-bg-tertiary);
        }
        .venta-sidebar-lock-icon {
          margin-left: auto;
          color: var(--color-text-muted);
          flex-shrink: 0;
        }

        .venta-sidebar-coming-soon {
          font-size: 0.5625rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--color-text-muted);
          background: var(--color-bg-tertiary);
          padding: 0.0625rem 0.3125rem;
          border-radius: 9999px;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
}

export default function VentaSidebar() {
  const { t } = useTranslation('venta');
  const location = useLocation();
  const { isDark } = useTheme();
  const { user } = useAuthStore();
  const logo = isDark ? logoLight : logoDark;
  const isSupportSession = !!useSupportSessionStore((s) => s.activeSession);
  const { hasFeature } = useHasFeature();
  const isOrgAdmin = user?.role === 'ORG_ADMIN' || user?.role === 'SUPER_ADMIN';

  // Load permissions from backend into authStore (for PermissionGate usage elsewhere)
  useMyPermissions();

  // Fetch menu tree from API (already permission-filtered server-side)
  const { data: menuTree } = useMyMenus();

  const { data: alertSummary } = useLowStockSummary();
  const alertCount = alertSummary?.totalAlerts ?? 0;

  const { data: transferSummary } = useTransferSummary();
  const pendingCount = transferSummary?.pendingApprovals ?? 0;

  const enrichedSections = useMemo(() => {
    if (!menuTree) return [];
    const apiSections = mapMenuTreeToSections(menuTree, t, alertCount, pendingCount);
    return apiSections
      .map((section) => ({
        ...section,
        items: section.items
          .filter((item) => !(isSupportSession && item.href === '/venta/support-access'))
          .filter((item) => !item.orgAdminOnly || isOrgAdmin)
          .filter((item) => isOrgAdmin || !item.feature || hasFeature('VENTA', item.feature))
          .map((item) => ({
            ...item,
            locked: isOrgAdmin && !!item.feature && !hasFeature('VENTA', item.feature),
          })),
      }))
      .filter((section) => section.items.length > 0);
  }, [menuTree, t, alertCount, pendingCount, isSupportSession, hasFeature, isOrgAdmin]);

  return (
    <nav className="venta-sidebar">
      {/* Logo Header */}
      <div className="venta-sidebar-header">
        <div className="venta-sidebar-brand">
          <div className="venta-sidebar-logo">
            <img src={logo} alt="VentaCoreX" />
          </div>
          <div>
            <div className="venta-sidebar-title">VentaCoreX</div>
            <div className="venta-sidebar-subtitle">{t('venta.module.name')}</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="venta-sidebar-nav">
        {enrichedSections.map((section) => (
          <SidebarSection
            key={section.id}
            id={section.id}
            title={t(section.titleKey)}
            items={section.items}
            currentPath={location.pathname}
          />
        ))}
      </div>

      {/* User Footer */}
      {user && (
        <div className="venta-sidebar-footer">
          <div className="venta-sidebar-user-avatar">
            {(user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')}
          </div>
          <div className="venta-sidebar-user-info">
            <div className="venta-sidebar-user-name">{user.firstName} {user.lastName}</div>
            <div className="venta-sidebar-user-email">{user.email}</div>
          </div>
        </div>
      )}

      <style>{`
        .venta-sidebar {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .venta-sidebar-header {
          padding: 12px 12px 12px 12px;
          border-bottom: 1px solid var(--color-border-subtle);
          margin-bottom: 0.25rem;
        }

        .venta-sidebar-brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .venta-sidebar-logo {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .venta-sidebar-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .venta-sidebar-title {
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--color-text-primary);
          line-height: 1.2;
        }

        .venta-sidebar-subtitle {
          font-size: 0.6875rem;
          color: var(--color-text-muted);
        }

        .venta-sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 0.25rem 0;
        }

        .venta-sidebar-footer {
          border-top: 1px solid var(--color-border-subtle);
          padding: 10px 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .venta-sidebar-user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.6875rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        .venta-sidebar-user-info {
          overflow: hidden;
        }

        .venta-sidebar-user-name {
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--color-text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .venta-sidebar-user-email {
          font-size: 0.6875rem;
          color: var(--color-text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .venta-sidebar-section {
          margin-bottom: 0.25rem;
        }

        .venta-sidebar-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.375rem 0.75rem;
          font-size: 0.675rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-muted);
          cursor: pointer;
          border-radius: 0.375rem;
          transition: color 0.15s;
          user-select: none;
        }

        .venta-sidebar-section-header:hover {
          color: var(--color-text-secondary);
        }

        .venta-sidebar-section-header .chevron {
          transition: transform 0.2s ease;
        }

        .venta-sidebar-section-header .chevron.collapsed {
          transform: rotate(-90deg);
        }

        .venta-sidebar-item {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.5rem 0.75rem;
          padding-left: 1rem;
          font-size: 0.8125rem;
          color: var(--color-text-secondary);
          text-decoration: none;
          border-radius: 0.375rem;
          transition: all 0.15s ease;
          border-left: 2px solid transparent;
        }

        .venta-sidebar-item:hover {
          background: var(--sidebar-item-hover, var(--color-bg-hover));
          color: var(--color-text-primary);
        }

        .venta-sidebar-item.active {
          background: var(--sidebar-item-active, rgba(139, 0, 0, 0.1));
          color: var(--sidebar-item-active-text, var(--color-brand-primary));
          border-left-color: var(--sidebar-item-active-border, var(--color-brand-primary));
          font-weight: 500;
        }

        .venta-sidebar-item-icon {
          flex-shrink: 0;
          opacity: 0.7;
        }

        .venta-sidebar-item.active .venta-sidebar-item-icon {
          opacity: 1;
        }

        .venta-sidebar-item-label {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .venta-sidebar-badge {
          font-size: 0.6875rem;
          font-weight: 600;
          padding: 0.0625rem 0.375rem;
          border-radius: 9999px;
          background: var(--color-bg-tertiary);
          color: var(--color-text-muted);
          min-width: 1.25rem;
          text-align: center;
        }

        .venta-sidebar-badge.danger {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .venta-sidebar-item.disabled {
          opacity: 0.45;
          cursor: not-allowed;
          pointer-events: none;
        }

        .venta-sidebar-item.locked {
          opacity: 0.5;
          cursor: pointer;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          font: inherit;
        }
        .venta-sidebar-item.locked:hover {
          opacity: 0.7;
          background: var(--color-bg-tertiary);
        }
        .venta-sidebar-lock-icon {
          margin-left: auto;
          color: var(--color-text-muted);
          flex-shrink: 0;
        }

        .venta-sidebar-coming-soon {
          font-size: 0.5625rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--color-text-muted);
          background: var(--color-bg-tertiary);
          padding: 0.0625rem 0.3125rem;
          border-radius: 9999px;
          flex-shrink: 0;
        }
      `}</style>
    </nav>
  );
}

function SidebarSection({
  id,
  title,
  items,
  currentPath,
}: {
  id: string;
  title: string;
  items: NavItem[];
  currentPath: string;
}) {
  const { t } = useTranslation('venta');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  // When the shell is an icons-only rail the section headers are hidden, so a
  // closed section would be unreachable — force every section open instead.
  const shellCollapsed = useSidebarCollapsed();
  const [ctxMenu, setCtxMenu] = useState<{ href: string; x: number; y: number } | null>(null);
  const [lockedFeature, setLockedFeature] = useState<string | null>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setCtxMenu({ href, x: e.clientX, y: e.clientY });
  }, []);

  const featureDisplayName = lockedFeature
    ? t(`venta.admin.roles.gatedPermission.features.${lockedFeature}`, lockedFeature)
    : '';

  return (
    <div className="venta-sidebar-section">
      <button
        className="venta-sidebar-section-header"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span>{title}</span>
        <ChevronDown
          size={14}
          className={cn('chevron', !isOpen && 'collapsed')}
        />
      </button>
      {(isOpen || shellCollapsed) && (
        <div>
          {items.map((item) =>
            item.disabled ? (
              <div
                key={item.href}
                className="venta-sidebar-item disabled"
                title={t('venta.common.comingSoon')}
                onContextMenu={(e) => handleContextMenu(e, item.href)}
              >
                <item.icon size={16} className="venta-sidebar-item-icon" />
                <span className="venta-sidebar-item-label">{t(item.labelKey)}</span>
                <span className="venta-sidebar-coming-soon">{t('venta.common.soon')}</span>
              </div>
            ) : item.locked ? (
              <button
                key={item.href}
                type="button"
                className="venta-sidebar-item locked"
                title={t('venta.admin.roles.gatedPermission.lockedHint', {
                  featureName: t(`venta.admin.roles.gatedPermission.features.${item.feature}`, item.feature ?? ''),
                })}
                onClick={() => setLockedFeature(item.feature ?? null)}
              >
                <item.icon size={16} className="venta-sidebar-item-icon" />
                <span className="venta-sidebar-item-label">{t(item.labelKey)}</span>
                <Lock size={12} className="venta-sidebar-lock-icon" />
              </button>
            ) : (
              <NavLink
                key={item.href}
                to={item.href}
                end
                className={({ isActive }) =>
                  cn('venta-sidebar-item', isActive && 'active')
                }
                title={t(item.labelKey)}
                onContextMenu={(e) => handleContextMenu(e, item.href)}
              >
                <item.icon size={16} className="venta-sidebar-item-icon" />
                <span className="venta-sidebar-item-label">{t(item.labelKey)}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span
                    className={cn(
                      'venta-sidebar-badge',
                      item.badgeVariant === 'danger' && 'danger'
                    )}
                  >
                    {item.badge}
                  </span>
                )}
              </NavLink>
            )
          )}
        </div>
      )}
      {ctxMenu && (
        <FavoritesContextMenu
          href={ctxMenu.href}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {/* Paid Feature dialog for locked menu items */}
      {lockedFeature && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLockedFeature(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-subtle)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              padding: '1.5rem',
              maxWidth: '400px',
              width: '90%',
            }}
          >
            <h3
              style={{
                margin: '0 0 0.75rem',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-heading)',
              }}
            >
              {t('venta.admin.roles.gatedPermission.dialogTitle')}
            </h3>
            <p
              style={{
                margin: '0 0 1.25rem',
                color: 'var(--color-text-secondary)',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              {t('venta.admin.roles.gatedPermission.dialogBody', { featureName: featureDisplayName })}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setLockedFeature(null)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--color-border-default)',
                  borderRadius: 'var(--radius-md)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                {t('venta.admin.roles.gatedPermission.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  navigate('/venta/billing/addons');
                  setLockedFeature(null);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-on-brand)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {t('venta.admin.roles.gatedPermission.viewAddons')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
